import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { RootState, BasePageProps, ButtonStylized, ButtonStylizedType, emitGameEvent } from '@mental-mint/shared-ui';
import { getOrdinalSuffix, logger } from '@mental-mint/shared-utils';
import * as Lucide from 'lucide-react';
import { getCurrentPuzzle, WordDriftPuzzle, getDictionary, getPlayableWordInfo } from '../../WordDriftUtils';
import { createGameWord } from '../../Utils/WordUtils';
import { LevelSelectionButton } from '../../Components/LevelSelectionButton';
import { WordDisplay } from '../../Components/WordDisplay/WordDisplay';

interface PagePuzzleIntroProps extends BasePageProps {
  /** Optional puzzle to display. If not provided, will load current puzzle */
  puzzle?: WordDriftPuzzle;
  /** Callback when user dismisses the intro (e.g., to start the game timer) */
  onDismiss?: () => void;
}

/**
 * Puzzle intro page that displays today's puzzle or the currently selected puzzle.
 * Shows the start and goal words with instructions.
 */
export const PagePuzzleIntro: React.FC<PagePuzzleIntroProps> = ({
  onRequestClose,
  puzzle: initialPuzzle,
  onDismiss
}) => {
  const { currentGame: game } = useSelector((state: RootState) => state.gameSession);
  const dispatch = useDispatch();
  const [puzzle, setPuzzle] = useState<WordDriftPuzzle | null>(initialPuzzle || null);
  const [isLoading, setIsLoading] = useState(!initialPuzzle);
  const [showDefinitions, setShowDefinitions] = useState(false);
  
  // Load puzzles and get the current puzzle if not provided via navigationProps
  useEffect(() => {
    if (puzzle) return;
    
    const loadPuzzleData = async () => {
      try {
        // This will use cached puzzles if already loaded, or start loading if not
        // The promise deduplication in loadPuzzles() ensures we don't double-fetch
        const currentPuzzle = await getCurrentPuzzle();
        setPuzzle(currentPuzzle);
        setIsLoading(false);
      } catch (error) {
        logger.error('Failed to load puzzle:', error);
        setIsLoading(false);
      }
    };
    
    // Start loading immediately without blocking render
    loadPuzzleData();
  }, [puzzle]);
  
  // If we have an initial puzzle, clear loading state immediately for maximum responsiveness
  useEffect(() => {
    if (initialPuzzle) {
      setIsLoading(false);
    }
  }, [initialPuzzle]);
  
  if (!game) {
    return <div />;
  }
  
  if (isLoading || !puzzle) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '200px'
      }}>
        <div style={{
          fontSize: '18px',
          color: game.colorPrimary || '#8B4513',
          marginBottom: '20px'
        }}>
          Loading puzzle...
        </div>
        <ClipLoader 
          size={32} 
          color={game.colorPrimary || '#8B4513'} 
        />
      </div>
    );
  }
  
  // Format the puzzle date for display
  const getPuzzleTitle = () => {
    if (!puzzle.date) {
      return "Today's Words";
    }
    
    const [year, month, day] = puzzle.date.split('-').map(Number);
    const puzzleDate = new Date(year, month - 1, day);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    puzzleDate.setHours(0, 0, 0, 0);
    
    if (puzzleDate.getTime() === today.getTime()) {
      return "Today's Words";
    }
    
    const monthName = puzzleDate.toLocaleDateString('en-US', { month: 'long' });
    return `${monthName} ${day}${getOrdinalSuffix(day)} Words`;
  };
  
  // Render goal word definitions using WordDisplay component
  const renderGoalDefinitions = () => {
    if (!showDefinitions) return null;
    
    const goalWord = puzzle.goalWord.toLowerCase();
    
    try {
      // Create proper GameWord object and get word info
      const goalGameWord = createGameWord(goalWord, getDictionary());
      const wordInfo = getPlayableWordInfo(goalGameWord, true, "");
      
      if (!wordInfo || !wordInfo.synsets || wordInfo.synsets.length === 0) {
        return (
          <div style={{
            fontSize: '0.95rem',
            color: '#666',
            fontStyle: 'italic',
            padding: '1rem',
            textAlign: 'center'
          }}>
            No definitions available for this word.
          </div>
        );
      }
      
      // Create a modified wordInfo with no clickable words
      const nonClickableWordInfo = {
        ...wordInfo,
        clickableWords: [] // Make all words non-clickable
      };
      
      // Create a minimal game state just for WordDisplay rendering
      const mockGameState = {
        goalWord: "", // Empty so no word highlights as goal
        isComplete: false,
        isWon: false,
        path: [],
        currentWordPlayableInfo: nonClickableWordInfo,
        currentPathDistanceToGoal: null,
        bestNextWord: null,
        moveCount: 0,
        hintsUsed: 0,
        hintUsedForCurrentWord: false,
        timeLeftSeconds: 300,
        timeLimitSeconds: 300,
        puzzle: puzzle,
        optimalPath: null
      };
      
      return (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '8px',
          border: `1px solid ${game.colorPrimary}33`,
          maxHeight: '400px',
          overflowY: 'auto',
          boxShadow: '0 1px 6px rgba(0, 0, 0, 0.3)'
        }}>
          <WordDisplay
            wordInfo={nonClickableWordInfo}
            gameState={mockGameState as any}
            gameColor={game.colorPrimary}
            onWordSelection={() => {}} // No-op, words won't be clickable
            grayedOutWords={new Set()}
            makeWordsUnclickable={true}
          />
        </div>
      );
    } catch (error) {
      logger.error('Error rendering goal definitions:', error);
      return null;
    }
  };
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1.5rem',
      padding: '1.5rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      {/* Title with date */}
      <div style={{
        fontSize: '1.8rem',
        color: game.colorPrimary,
        fontWeight: '600',
        textAlign: 'center',
        fontFamily: game.fontPrimary
      }}>
        {getPuzzleTitle()}
      </div>
      
      {/* Puzzle Display */}
      <LevelSelectionButton puzzle={puzzle} />
      
      {/* Instructions */}
      <div style={{
        fontSize: '1.1rem',
        color: '#4a5568',
        lineHeight: '1.6',
        textAlign: 'center',
        fontFamily: game.fontSecondary
      }}>
        Starting from the definition of <strong style={{color: game.colorPrimary}}>{puzzle.startWord}</strong>,
        click on links until you reach the word <strong style={{color: game.colorPrimary}}>{puzzle.goalWord}</strong>.
      </div>
      
      {/* Time limit */}
      <div style={{
        fontSize: '1rem',
        color: game.colorPrimary,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontFamily: game.fontSecondary
      }}>
        <Lucide.Clock size={20} strokeWidth={2} />
        You have 5 minutes
      </div>
      
      {/* View Goal Definition Toggle */}
      <ButtonStylized
        buttonType={ButtonStylizedType.NoStyling}
        onClick={() => setShowDefinitions(!showDefinitions)}
        icon={showDefinitions ? <Lucide.ChevronUp size={16} /> : <Lucide.ChevronDown size={16} />}
        additionalStyles={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontSize: '0.9rem',
          color: game.colorPrimary,
          background: 'rgba(255, 255, 255, 0.7)',
          border: `1.5px solid ${game.colorPrimary}`,
          borderRadius: '6px',
          padding: '0.9rem 2rem',
          cursor: 'pointer',
          fontFamily: game.fontSecondary,
          fontWeight: '500',
          transition: 'all 0.2s ease',
          width: '100%',
          maxWidth: '280px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = `${game.colorPrimary}15`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
        }}
      >
        {showDefinitions ? "Hide Goal Definition" : "View Goal Definition"}
      </ButtonStylized>
      
      {/* Goal Word Definitions */}
      {renderGoalDefinitions()}
      
      {/* Got it button */}
      <ButtonStylized
        buttonType={ButtonStylizedType.Primary}
        onClick={() => {
          // Emit event for PagePlay to listen to
          dispatch(emitGameEvent({ 
            type: 'word-drift:puzzle-intro-dismissed',
            gameId: game.metadata.gameId
          }));
          onDismiss?.(); // Keep backward compatibility if needed
          onRequestClose?.(); // Close the modal
        }}
        text="Got it!"
        additionalStyles={{
          fontSize: '1.1rem',
          padding: '0.75rem 2rem',
          fontFamily: game.fontPrimary,
          fontWeight: '300',
          width: '100%',
          maxWidth: '280px',
        }}
      />
    </div>
  );
};

export default PagePuzzleIntro;
